import { Workbox } from 'workbox-window';
import { isLocalEnv } from '../../common/helpers/environment';
import { Logger } from 'Common/appshell_types/loggerTypes';

interface ServiceWorkerConfig {
    translationsVersion: string;
    buildVersion: string;
    bellaApiBaseUrl: string;
    env: string;
}
// Define message types
const CACHE_URLS_MESSAGE_TYPE = 'CACHE_URLS';
const CHECK_IF_VERSION_CHANGED_MESSAGE_TYPE = 'VERSION_CHANGE';
const RELOAD_PAGE_MESSAGE_TYPE = 'RELOAD_PAGE';

const CHECK_IF_VERSION_CHANGED_TIME = 1000 * 60 * 5; // 5 minutes

class ServiceWorkerManager {
    private wb: Workbox;

    private logger: Logger;

    private isEnabled: boolean;

    private config: ServiceWorkerConfig;

    public constructor(logger: Logger, config: ServiceWorkerConfig) {
        this.wb = new Workbox('/service-worker.js');
        this.logger = logger;

        // Todo: Check Feature toggle as well
        this.isEnabled = !isLocalEnv(config.env);
        this.config = config;
    }

    private runSafe(cb: Function, isCleanup?: boolean) {
        if ('serviceWorker' in navigator && (this.isEnabled || isCleanup)) {
            try {
                cb();
            } catch (error) {
                this.logger.error(error);
            }
        }
    }

    private register() {
        this.runSafe(() => this.wb.register());
    }

    public addUrlsToCache(urlsToCache: string[]) {
        // Send that list of URLs to the routers in the service worker.
        this.runSafe(() => {
            this.wb.messageSW({
                type: CACHE_URLS_MESSAGE_TYPE,
                payload: { urlsToCache },
            });
        });
    }

    public reloadIfVersionChanged() {
        this.runSafe(async () => {
            const res = await this.wb.messageSW({
                type: CHECK_IF_VERSION_CHANGED_MESSAGE_TYPE,
                payload: this.config,
            });

            // @ts-ignore
            if (res === RELOAD_PAGE_MESSAGE_TYPE) {
                window.location.reload();
            }
        });
    }

    private precacheUrls() {
        this.runSafe(() => {
            const urlsToCache = [window.location.href];
            this.addUrlsToCache(urlsToCache);
        });
    }

    private deregister() {
        this.runSafe(() => {
            navigator.serviceWorker
                .getRegistrations()
                .then(registrations => {
                    const toDeregister = Array.from(registrations);

                    toDeregister.forEach(registration => {
                        registration.unregister();
                    });

                    // Delete caches
                    if (toDeregister.length) {
                        caches
                            .keys()
                            .then(cacheNames => {
                                return Promise.all(
                                    cacheNames.map(cacheName => {
                                        return caches.delete(cacheName);
                                    })
                                );
                            })
                            .catch(e => this.logger.error('ServiceWorkerManager delete caches', e));
                    }
                })
                .catch(e => this.logger.error('ServiceWorkerManager deregister', e));
        }, true);
    }

    public bootstrap() {
        if (this.isEnabled) {
            this.register();
            this.precacheUrls();
            setInterval(() => {
                this.reloadIfVersionChanged();
            }, CHECK_IF_VERSION_CHANGED_TIME);
        } else {
            this.deregister();
        }
    }
}

export default ServiceWorkerManager;
